import { useState, type FormEvent } from 'react';
import { ApiError } from '../../api/client';
import type { CtResourceKind, CtResourceLink, SaveCtResourceRequest } from '../../api/types';
import { Modal } from '../../components/Modal';

const MAX_UPLOAD_MB = 10;

interface CtResourceDialogProps {
  /** The entry being edited, or null to add a new one. */
  editing: CtResourceLink | null;
  onSave: (request: SaveCtResourceRequest) => Promise<void>;
  onClose: () => void;
}

type FieldErrors = Partial<Record<'name' | 'description' | 'kind' | 'url' | 'file', string>>;

/**
 * Add or edit one entry on a reference page: name, description, type, and a PDF, a link, or both.
 *
 * Mounted only while open, so every opening starts from the entry as it is now rather than from
 * whatever was typed the last time. The API checks everything again; the checks here only save a
 * round trip for the obvious ones, and the API's own messages land under the same fields.
 */
export function CtResourceDialog({ editing, onSave, onClose }: CtResourceDialogProps) {
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  // The select holds 'document', 'form' or 'other'; with 'other' the text box beside it is the type.
  const initialKind: CtResourceKind = editing?.kind ?? 'document';
  const isBuiltIn = initialKind === 'document' || initialKind === 'form';
  const [kindChoice, setKindChoice] = useState(isBuiltIn ? initialKind : 'other');
  const [customKind, setCustomKind] = useState(isBuiltIn ? '' : initialKind);
  const kind = kindChoice === 'other' ? customKind.trim() : kindChoice;
  const [url, setUrl] = useState(editing?.url ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [removeDocument, setRemoveDocument] = useState(false);

  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const existingFileName = editing?.hasDocument ? editing.documentFileName ?? 'document.pdf' : null;
  const keepsDocument = file !== null || (existingFileName !== null && !removeDocument);

  function validate(): FieldErrors {
    const found: FieldErrors = {};
    const trimmedUrl = url.trim();

    if (name.trim() === '') found.name = 'Give it a name.';

    if (kindChoice === 'other' && kind === '') {
      found.kind = 'Type what it is, or choose Document or Form.';
    }

    if (trimmedUrl !== '' && !/^https?:\/\/\S+$/i.test(trimmedUrl)) {
      found.url = 'Paste a full link, starting with https://.';
    }

    if (file && !file.name.toLowerCase().endsWith('.pdf')) {
      found.file = 'Only PDF files can be uploaded.';
    } else if (file && file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      found.file = `The file is larger than the ${MAX_UPLOAD_MB} MB limit.`;
    }

    if (!found.url && !found.file && trimmedUrl === '' && !keepsDocument) {
      found.file = 'Add a PDF, a link, or both.';
    }

    return found;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();

    const found = validate();
    setErrors(found);
    setFormError(null);
    if (Object.keys(found).length > 0) return;

    setBusy(true);

    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        kind,
        url: url.trim(),
        file,
        removeDocument,
      });
    } catch (error: unknown) {
      if (error instanceof ApiError && Object.keys(error.fieldErrors).length > 0) {
        setErrors(
          Object.fromEntries(
            Object.entries(error.fieldErrors).map(([key, messages]) => [key, messages[0]]),
          ) as FieldErrors,
        );
      } else {
        console.error('Could not save the entry', error);
        setFormError('It could not be saved. Try again in a moment.');
      }
    } finally {
      setBusy(false);
    }
  }

  const formId = 'ct-resource-form';

  return (
    <Modal
      open
      title={editing ? 'Edit document' : 'Add document'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button button--quiet" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form={formId} className="button button--primary" disabled={busy}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Add'}
          </button>
        </>
      }
    >
      <form id={formId} className="ct-resource-form" onSubmit={submit} noValidate>
        {formError && (
          <p className="page-message page-message--error" role="alert">
            {formError}
          </p>
        )}

        <div className="field">
          <label className="field__label" htmlFor="ctResourceName">
            Name <span className="field__required">*</span>
          </label>
          <input
            id="ctResourceName"
            className={errors.name ? 'control control--invalid' : 'control'}
            value={name}
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
          />
          {errors.name && <p className="field__error">{errors.name}</p>}
        </div>

        <div className="field">
          <label className="field__label" htmlFor="ctResourceDescription">
            Description <span className="field__optional">optional</span>
          </label>
          <textarea
            id="ctResourceDescription"
            className={errors.description ? 'control control--invalid' : 'control'}
            rows={2}
            value={description}
            maxLength={1000}
            placeholder="What it is and who it is for"
            onChange={(e) => setDescription(e.target.value)}
          />
          {errors.description && <p className="field__error">{errors.description}</p>}
        </div>

        <div className="field">
          <label className="field__label" htmlFor="ctResourceKind">
            Type
          </label>
          {/* Label, control, message — the custom box goes inside the control slot so the field
              keeps its three parts. */}
          <div className="ct-resource-form__kind">
            <select
              id="ctResourceKind"
              className={errors.kind && kindChoice !== 'other' ? 'control control--invalid' : 'control'}
              value={kindChoice}
              onChange={(e) => setKindChoice(e.target.value)}
            >
              <option value="document">Document — something to read</option>
              <option value="form">Form — something to fill in</option>
              <option value="other">Other…</option>
            </select>
            {kindChoice === 'other' && (
              <input
                aria-label="Type"
                className={errors.kind ? 'control control--invalid' : 'control'}
                value={customKind}
                maxLength={50}
                placeholder="e.g. Video, Checklist"
                onChange={(e) => setCustomKind(e.target.value)}
              />
            )}
          </div>
          {errors.kind && <p className="field__error">{errors.kind}</p>}
        </div>

        <div className="field">
          <label className="field__label" htmlFor="ctResourceUrl">
            Link <span className="field__optional">optional</span>
          </label>
          <input
            id="ctResourceUrl"
            type="url"
            className={errors.url ? 'control control--invalid' : 'control'}
            value={url}
            maxLength={2048}
            placeholder="https://…"
            onChange={(e) => setUrl(e.target.value)}
          />
          {errors.url ? (
            <p className="field__error">{errors.url}</p>
          ) : (
            <p className="field__help">SharePoint, Smartsheet or any other page. Opens in a new tab.</p>
          )}
        </div>

        <div className="field">
          <label className="field__label" htmlFor="ctResourceFile">
            PDF <span className="field__optional">optional</span>
          </label>
          <input
            id="ctResourceFile"
            type="file"
            accept=".pdf,application/pdf"
            className={errors.file ? 'control control--invalid' : 'control'}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {errors.file ? (
            <p className="field__error">{errors.file}</p>
          ) : file ? (
            <p className="field__help">
              {existingFileName ? 'Replaces ' : 'Uploads '}
              <strong>{existingFileName ?? file.name}</strong>
              {existingFileName ? ` with ${file.name}.` : '.'}
            </p>
          ) : existingFileName ? (
            <p className="field__help">
              Current: <strong>{existingFileName}</strong>. Choose a file only to replace it.
            </p>
          ) : (
            <p className="field__help">Gives the entry a Preview PDF button. Up to {MAX_UPLOAD_MB} MB.</p>
          )}
        </div>

        {/* Only offered when there is something to remove and nothing chosen to replace it with —
            choosing a file already means the old one goes. */}
        {existingFileName && !file && (
          <label className="checkbox">
            <input
              type="checkbox"
              checked={removeDocument}
              onChange={(e) => setRemoveDocument(e.target.checked)}
            />
            Remove the PDF and keep only the link
          </label>
        )}

        <p className="field__help">An entry needs a PDF, a link, or both.</p>

        {editing && (
          <p className="ct-resource-form__changed">
            Last changed by {editing.updatedBy} on {new Date(editing.updatedAtUtc).toLocaleDateString()}.
          </p>
        )}
      </form>
    </Modal>
  );
}
